import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Search } from "lucide-react";

import { cn } from "@/lib/utils";
import { search } from "@/api/search";
import { type SearchResultItem } from "@/api/types";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

// ─── Task 23: global search (Ctrl+K) ───────────────────────────────────────
//
// Hand-rolled command palette (no `cmdk` — not installed, not needed: this
// is a text input + a flat, keyboard-navigable results list). Mounted once
// in `_app.tsx`'s AppLayout, which owns the open/close state and the
// keydown listener that toggles it.

const MIN_QUERY_LENGTH = 2;
/** No existing debounce precedent in this app; 300ms is a standard typeahead value. */
const DEBOUNCE_MS = 300;

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const GROUPS: Array<{
  key: "invoices" | "merchants" | "transactions" | "addresses" | "channels" | "channelPayments";
  heading: string;
}> = [
  { key: "invoices", heading: "Invoices" },
  { key: "merchants", heading: "Merchants" },
  { key: "transactions", heading: "Transactions" },
  { key: "addresses", heading: "Addresses" },
  // Task 14 (feature-031 Phase B) — CHANNELS_VIEWER-gated groups, same
  // absent-vs-empty-array server-side omission as every other group here.
  { key: "channels", heading: "Channels" },
  { key: "channelPayments", heading: "Channel payments" },
];

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Reset the input/highlight whenever the dialog opens or closes, and
  // auto-focus the input on open.
  useEffect(() => {
    if (!open) return undefined;

    setValue("");
    setDebouncedValue("");
    setHighlightedIndex(0);
    // Radix mounts the content on the next tick — defer focus one frame.
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Debounce: only fire the search DEBOUNCE_MS after the admin stops typing.
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [value]);

  const trimmed = debouncedValue.trim();
  const queryEnabled = open && trimmed.length >= MIN_QUERY_LENGTH;

  const { data, isFetching, isError } = useQuery({
    queryKey: ["search", trimmed],
    queryFn: () => search(trimmed),
    enabled: queryEnabled,
  });

  // Only groups the response actually included AND that have at least one
  // hit are rendered — an absent key means the caller's role doesn't permit
  // that group (no heading, no message, server-side omission is already the
  // access-control signal); an empty-but-present array is possible but
  // unlikely in practice, and is treated the same as absent (no empty
  // headings cluttering the list).
  const visibleGroups = useMemo(() => {
    if (!data) return [];
    return GROUPS.filter((group) => (data[group.key]?.length ?? 0) > 0).map((group) => ({
      ...group,
      items: data[group.key] as SearchResultItem[],
    }));
  }, [data]);

  const flatItems = useMemo<SearchResultItem[]>(
    () => visibleGroups.flatMap((group) => group.items),
    [visibleGroups],
  );

  // Keep the highlighted index in range whenever the result set changes.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [flatItems.length, trimmed]);

  const activate = (item: SearchResultItem) => {
    onOpenChange(false);
    // `item.path` is a runtime string, not a route literal. Verified via
    // `pnpm typecheck` (not assumed) that TanStack Router's typed
    // `navigate({ to })` still accepts a plain `string`-typed variable here
    // — same permissive behavior `_app.tsx`'s `NavItem.to: string` already
    // relies on via `<Link to={item.to}>`. No `window.location.assign`
    // fallback needed.
    //
    // `item.query` (e.g. transaction hits' `{ transactionId }`) is passed
    // through as the `search` param — same `{ path, query }` deep-link shape
    // `_app.dashboard.tsx`'s `HealthCountStatCard` already establishes via
    // `<Link to={filter.path} search={filter.query}>`, adapted here for
    // `navigate()` since this is a programmatic activation, not a link.
    navigate({ to: item.path, search: item.query });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (flatItems.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((i) => (i + 1) % flatItems.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((i) => (i - 1 + flatItems.length) % flatItems.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const item = flatItems[highlightedIndex];
      if (item) activate(item);
    }
    // Escape is left to the underlying Dialog primitive, which already
    // closes on Escape by default.
  };

  let flatCursor = -1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-lg">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search invoices, merchants, transactions, addresses…"
            className="h-8 border-0 shadow-none focus-visible:ring-0 px-0"
          />
        </div>

        <div className="max-h-[420px] overflow-y-auto p-2">
          {trimmed.length < MIN_QUERY_LENGTH && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Type at least {MIN_QUERY_LENGTH} characters to search.
            </div>
          )}

          {queryEnabled && isFetching && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching…
            </div>
          )}

          {queryEnabled && !isFetching && isError && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Something went wrong running that search. Try again.
            </div>
          )}

          {queryEnabled && !isFetching && !isError && flatItems.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">No results.</div>
          )}

          {queryEnabled && !isFetching && !isError && flatItems.length > 0 && (
            <div className="space-y-3">
              {visibleGroups.map((group) => (
                <div key={group.key}>
                  <div className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.heading}
                  </div>
                  <div className="space-y-0.5">
                    {group.items.map((item) => {
                      flatCursor += 1;
                      const isHighlighted = flatCursor === highlightedIndex;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => activate(item)}
                          onMouseEnter={() => setHighlightedIndex(flatCursor)}
                          className={cn(
                            "flex w-full items-center rounded-md px-2 py-2 text-left text-sm transition-colors",
                            isHighlighted
                              ? "bg-accent text-accent-foreground"
                              : "text-foreground hover:bg-accent hover:text-accent-foreground",
                          )}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
